import { useEffect, useState, useRef } from 'react'
import { callApi, ThrowError } from '@/api/callApi'
import { API_ENDPOINT, API_URL } from '@/api/constant'
import { formatDateTime, formatRand, formatStatus, GetFilterUrl, downloadExcel } from '@/utility/helper'
import Pagination from '@/components/common/Pagination'
import Button from '@/components/common/Button'
import { IPageInfo, IFilter } from '@/interfaces/common.interface'
import { RoutesLink } from '@/utility/RouteLinks'
import { useFilter } from '@/hooks/useFilter'
import { MainHeading } from '@/components/common/MainHeading'
import { Layout } from '@/components/Layout/Layout'
import CommonTable, { TableColumn } from '@/components/common/CommonTable'
import { StatusBadge } from '@/components/common/StatusBadge'
import { CommonSelect } from '@/components/common/CommonSelect'
import { Eye, Download, Truck } from 'lucide-react'
import { format } from 'date-fns'
import { Range } from 'react-date-range'
import { IOrder, IOrderResponse } from '@/interfaces/Order/order.interface'
import { useRoleNavigate } from '@/hooks/useRoleNavigate'
import { OrderAdvancedFilters } from '@/components/common/Filters/OrderAdvancedFilters'
import { usePermissions } from '@/hooks/usePermissions'
import { RESOURCES } from '@/context/permissions'
import MultipleOrdersDriverAssignment from './MultipleOrdersDriverAssignment'
import CommonModal from '@/components/common/CommonModal'


export const Orders = () => {
    const navigate = useRoleNavigate();
    const { isAdmin, canView, canExport, canEdit } = usePermissions()
    const [orders, setOrders] = useState<IOrder[]>([]);
    const [loading, setLoading] = useState(false);
    const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
    const [pageMetadata, setPageMetadata] = useState<IPageInfo>({
        limit: 10,
        page: 1,
        total: 0,
        totalPages: 0,
    });

    const { filters, updateFilter, handlePageChange, debouncedFilters, resetFilters, handlePageSizeChange } = useFilter<IFilter>({
        search: '',
        status: '',
        page: 1,
        limit: 10,
        storeId: '',
        startDate: '',
        endDate: '',
        deliveryType: '',
        userId: '',
        driverId: '',
        driverStatus: '',
        deliveryStatus: '',
        paymentMethod: '',
        paymentStatus: '',
        month: undefined,
        sort: '',
        storeSettlementStatus: '',
    });

    const [dateRange, setDateRange] = useState<Range[]>([
        {
            startDate: undefined,
            endDate: undefined,
            key: 'selection',
        },
    ]);

    const [loadingExport, setLoadingExport] = useState<boolean>(false);

    const handleExport = async () => {
        const queryParams = GetFilterUrl({
            status: filters.status,
            startDate: filters.startDate,
            endDate: filters.endDate,
            sort: filters.sort,
            storeId: filters.storeId,
            deliveryType: filters.deliveryType,
            userId: filters.userId,
            driverId: filters.driverId,
            driverStatus: filters.driverStatus,
            deliveryStatus: filters.deliveryStatus,
            paymentMethod: filters.paymentMethod,
            paymentStatus: filters.paymentStatus,
            storeSettlementStatus: filters.storeSettlementStatus,
            month: filters.month
        });
        const url = `${API_URL + API_ENDPOINT.orders}/export/excel?${queryParams}`;
        setLoadingExport(true);
        try {
            await downloadExcel(url, 'orders_export.xlsx');
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoadingExport(false);
        }
    };

    // Sync dateRange changes into the filter hook
    useEffect(() => {
        const start = dateRange[0]?.startDate;
        const end = dateRange[0]?.endDate;
        const formattedStart = start ? format(start, 'yyyy-MM-dd') : '';
        const formattedEnd = end ? format(end, 'yyyy-MM-dd') : '';

        if (formattedStart !== filters.startDate || formattedEnd !== filters.endDate) {
            updateFilter({ startDate: formattedStart, endDate: formattedEnd, page: 1 });
        }
    }, [dateRange]);
    const lastParamsRef = useRef<string>('');

    const fetchOrders = async () => {
        const currentParams = JSON.stringify(debouncedFilters);
        if (currentParams === lastParamsRef.current) return;
        lastParamsRef.current = currentParams;

        setLoading(true);
        try {
            const queryParams = GetFilterUrl(debouncedFilters);
            const response = await callApi<IOrderResponse>(
                `${API_URL}${API_ENDPOINT.orders}?${queryParams}`,
                { method: 'GET' }
            );

            if (response?.data) {
                setOrders(response?.data?.order || []);
                setPageMetadata(response?.data?.pageInfo || { limit: 10, page: 1, total: 0, totalPages: 0 });
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, [
        debouncedFilters.page,
        debouncedFilters.limit,
        debouncedFilters.search,
        debouncedFilters.sort,
        debouncedFilters.status,
        debouncedFilters.deliveryType,
        debouncedFilters.deliveryStatus,
        debouncedFilters.paymentMethod,
        debouncedFilters.paymentStatus,
        debouncedFilters.driverStatus,
        debouncedFilters.month,
        debouncedFilters.storeId,
        debouncedFilters.userId,
        debouncedFilters.driverId,
        debouncedFilters.startDate,
        debouncedFilters.endDate,
        debouncedFilters.storeSettlementStatus,
    ]);

    const columns: TableColumn<IOrder>[] = [
        {
            key: 'orderId',
            header: 'Order',
            render: (item) => (
                <div className="flex flex-col">
                    <span className="font-bold text-primary">#{item.orderId}</span>
                    <span className="text-[10px] text-muted-foreground">{formatDateTime(item.createdAt)}</span>
                </div>
            ),
        },
        {
            key: 'user',
            header: 'Customer',
            render: (item) => (
                <div className="flex flex-col">
                    <span className="font-medium text-foreground">{item.user?.fullName || '---'}</span>
                    <span className="text-xs text-muted-foreground">
                        {item.user?.countryCode} {item.user?.phone}
                    </span>
                </div>
            )
        },
        {
            key: 'store',
            header: 'Store',
            render: (item) => (
                <div className="flex flex-col">
                    <span className="font-medium text-foreground">{item.store?.name || '---'}</span>
                </div>
            )
        },
        {
            key: 'deliveryType',
            header: 'Type',
            render: (item) => (
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${item.deliveryType === 'delivery'
                    ? 'bg-blue-100 text-blue-700'
                    : item.deliveryType === 'takeaway'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-purple-100 text-purple-700'
                    }`}>
                    {item.deliveryType || '---'}
                </span>
            )
        },
        {
            key: 'paymentMethod',
            header: 'Payment',
            render: (item) => (
                <div className="flex flex-col">
                    <span className="text-sm font-semibold text-foreground">{item.paymentMethod || '---'}</span>
                    <span className={`text-[10px] font-semibold capitalize ${item.paymentStatus === 'paid' ? 'text-green-600' : 'text-amber-600'
                        }`}>
                        {formatStatus(item.paymentStatus)}
                    </span>
                </div>
            )
        },
        {
            key: 'finalAmount',
            header: 'Total',
            render: (item) => (
                <div className="flex flex-col">
                    <span className="font-bold text-foreground">{formatRand(item.finalAmount)}</span>
                    {Number(item.discountAmount) > 0 && (
                        <span className="text-[10px] text-green-600">−{formatRand(item.discountAmount)} off</span>
                    )}
                </div>
            ),
        },
        {
            key: 'storeSettlementStatus',
            header: 'Store Settlement',
            render: (item) => item.storeEarning?.status ? <StatusBadge status={item.storeEarning.status} /> : <span className="text-muted-foreground">-</span>,
        },
        {
            key: 'status',
            header: 'Status',
            render: (item) => <StatusBadge status={item.status} />,
        },
    ];


    return (
        <Layout>
            <MainHeading title="Orders" />

            <div className="mt-6 flex flex-col gap-6">
                <OrderAdvancedFilters
                    filters={filters}
                    updateFilter={updateFilter}
                    resetFilters={resetFilters}
                    isAdmin={isAdmin}
                    dateRange={dateRange}
                    setDateRange={setDateRange}
                />

                <div className="flex items-center justify-end gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                        {!isAdmin && canEdit(RESOURCES.ORDER) && <Button
                            variant="primary"
                            onClick={() => setIsAssignmentModalOpen(true)}
                            className="h-10 px-4 flex items-center gap-2 rounded-xl bg-green-600 hover:bg-green-700 text-white font-bold"
                            leftIcon={<Truck className="w-4 h-4" />}
                        >
                            Multiple Orders Driver Assignment
                        </Button>}
                        {canExport(RESOURCES.ORDER) && <Button
                            variant="outline"
                            onClick={() => handleExport()}
                            className="h-10 px-4 flex items-center gap-2 rounded-xl"
                            leftIcon={<Download className="w-4 h-4" />}
                            isLoading={loadingExport}
                        >
                            Export
                        </Button>}
                        <CommonSelect
                            value={filters.sort}
                            onValueChange={(val) => updateFilter({ sort: val, page: 1 })}
                            options={[
                                { value: "newest", label: "Newest First" },
                                { value: "oldest", label: "Oldest First" },
                                { value: "az", label: "Order ID (A-Z)" },
                                { value: "za", label: "Order ID (Z-A)" },
                                { value: "highest", label: "Highest Amount" },
                                { value: "lowest", label: "Lowest Amount" },
                                { value: "topRated", label: "Top Rated" },
                                { value: "lowRated", label: "Lowest Rated" },
                            ]}
                            triggerClassName="w-[180px] h-10 rounded-xl border-secondary/20 bg-card font-semibold"
                            placeholder="Sort By"
                        />
                    </div>
                </div>

                {/* Orders Table */}
                <CommonTable
                    columns={columns}
                    data={orders}
                    loading={loading}
                    actions={[
                        ...(canView(RESOURCES.ORDER) ? [{
                            text: 'View Order Details',
                            icon: <Eye strokeWidth={1.5} size={16} className="cursor-pointer" />,
                            onClick: (row) => navigate(RoutesLink.ViewOrders.replace(':id', row.id)),
                        }] : []),
                    ]}
                    emptyMessage="No orders found. Once customers place orders, they will appear here."
                />

                <Pagination
                    currentPage={filters.page || 1}
                    totalPages={pageMetadata.totalPages}
                    totalItems={pageMetadata.total}
                    itemsPerPage={pageMetadata.limit}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            </div>
            {/* Driver Assignment Modal */}
            <CommonModal
                open={isAssignmentModalOpen}
                onOpenChange={setIsAssignmentModalOpen}
                title="Multiple Orders Driver Assignment"
                size="xl"
            >
                <MultipleOrdersDriverAssignment onClose={() => setIsAssignmentModalOpen(false)} />
            </CommonModal>
        </Layout>
    )
}
